import {
  MutationCache,
  QueryCache,
  QueryClient,
  type DefaultOptions,
} from '@tanstack/react-query';

interface QueryClientOptions {
  /** Called for every query/mutation error. Use for Sentry logging etc. */
  onError?: (error: unknown, context?: string, queryName?: string) => void;
  /** Called when a 401 response is detected. Use for logout/redirect. */
  onUnauthorized?: () => void;
  /** Override individual query defaults (merged over the built-in defaults). */
  queryDefaults?: Partial<NonNullable<DefaultOptions['queries']>>;
}

function getStatusCode(error: unknown): number | undefined {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response
  ) {
    return (error.response as { status: number }).status;
  }
  return undefined;
}

function isNetworkError(error: unknown): boolean {
  return (
    error instanceof Error &&
    ('code' in error || error.message === 'Network Error')
  );
}

function retryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30000);
}

function queryRetry(failureCount: number, error: unknown): boolean {
  const status = getStatusCode(error);

  if (status && status >= 400 && status < 500) return false;

  if (status && status >= 500) return failureCount < 1;

  if (isNetworkError(error)) return failureCount < 3;

  return false;
}

export function createQueryClient(
  options: QueryClientOptions = {},
): QueryClient {
  const { onError, onUnauthorized, queryDefaults } = options;

  const handleError = (error: unknown, context: string, queryName?: string) => {
    const status = getStatusCode(error);

    if (status === 401 && onUnauthorized) {
      onUnauthorized();
      return;
    }

    onError?.(error, context, queryName);
  };

  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        handleError(
          error,
          `Query [${query.queryKey.toString()}]`,
          String(query.queryKey[0]),
        );
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const mutationName = mutation.options.mutationKey?.[0];
        handleError(
          error,
          `Mutation [${mutation.options.mutationKey?.toString() ?? 'unknown'}]`,
          mutationName === undefined ? undefined : String(mutationName),
        );
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: false,
        retry: queryRetry,
        retryDelay,
        ...queryDefaults,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
